import { imageMimeTypes } from 'constants/files';
import { List, OrderedSet, Set } from 'immutable';
import { RFIManagerValue } from 'providers/RFIManagerProvider';
import { SheetManagerValue } from 'providers/SheetManagerProvider';
import { UserInfoValue } from 'providers/UserInfoProvider';
import { PageId } from 'records/Page';
import { NewRFI } from 'records/NewRFI';
import { NewRFIAttachment } from 'records/NewRFIAttachment';
import { NewRFIForwardActivity } from 'records/NewRFIForwardActivity';
import { RFIAnswer } from 'records/RFIAnswer';
import { RFIFile } from 'records/RFIFile';
import * as toast from 'utils/toast';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { StudioData } from 'records/RFI';
import {
  AttachmentTypeEnum,
  RFIResponseRoleEnum,
  ViewRFIMachineServices,
} from './viewRfiMachineTypes';

interface CreateViewRfiServicesToastText {
  rfiDraftSaveError: {
    title: string;
    message: string;
  };

  rfiRespondError: {
    title: string;
    message: string;
  };

  rfiAttachDiscussionError: {
    title: string;
    message: string;
  };

  rfiReopenError: {
    title: string;
    message: string;
  };

  rfiDraftPublishError: {
    title: string;
    message: string;
  };

  rfiDraftDeleteError: {
    title: string;
    message: string;
  };
  rfiForwardMessageError: {
    title: string;
    message: string;
  };
  rfiForwardReplyError: {
    title: string;
    message: string;
  };
}

const createViewRfiServices = (
  rfiManager: RFIManagerValue,
  sheetManager: SheetManagerValue,
  userInfo: UserInfoValue,
  toastText: CreateViewRfiServicesToastText
): ViewRFIMachineServices => ({
  getRfiVersionList: (rfiid) => {
    const baseid = rfiid.split('+')[0];
    const rfiVersionList = rfiManager
      .getRFIVersionsByBaseId(baseid)
      .sortBy((rfi) => rfi.versionnumber)
      .reverse();

    return rfiVersionList;
  },
  getUserByEmail: (email: string) => userInfo.getUserByEmail(email),
  getUserRole: ({ rfi, currentUser, project }) => {
    if (project.permissions.activeProject && project.permissions.adminRfi) {
      return RFIResponseRoleEnum.admin;
    }

    if (rfi.assigneeEmail === currentUser.email) {
      return RFIResponseRoleEnum.assignee;
    }

    if (rfi?.forwardinglist.has(currentUser.email)) {
      return RFIResponseRoleEnum.consultant;
    }

    return RFIResponseRoleEnum.unauthorized;
  },
  getSheetVersionsFromPageIds: (pageids) =>
    sheetManager.getSheetVersions(pageids),
  updateAttachments: (event, rfiAttachments, projectId) => {
    switch (event.data.source) {
      case AttachmentTypeEnum.DRAWING:
        return {
          ...rfiAttachments,
          [event.data.source]: event.data.item as OrderedSet<string>,
          drawingSheetVersions: sheetManager.getSheetVersions(
            event.data.item as Set<string>
          ),
        };
      case AttachmentTypeEnum.FILE:
        return {
          ...rfiAttachments,
          [event.data.source]: getRfiFileList(
            event.data.item as FileList,
            rfiAttachments[AttachmentTypeEnum.FILE],
            projectId || ''
          ),
        };
      case AttachmentTypeEnum.STUDIO:
        return {
          ...rfiAttachments,
          [event.data.source]: event.data.item as List<StudioProjectFileData>,
          studioData: event.data.meta as StudioData,
        };
      default:
        return {
          ...rfiAttachments,
          [event.data.source]: event.data.item,
        };
    }
  },
  removeAttachments: (event, rfiAttachments) => {
    switch (event.data.source) {
      case AttachmentTypeEnum.DRAWING:
        return {
          ...rfiAttachments,
          [event.data.source]: rfiAttachments[
            AttachmentTypeEnum.DRAWING
          ].remove(event.data.item as PageId),
          drawingSheetVersions: sheetManager.getSheetVersions(
            rfiAttachments[AttachmentTypeEnum.DRAWING].remove(
              event.data.item as PageId
            )
          ),
        };
      case AttachmentTypeEnum.STUDIO:
        return {
          ...rfiAttachments,
          [event.data.source]: rfiAttachments[AttachmentTypeEnum.STUDIO].filter(
            (item) => item !== event.data.item
          ),
          studioData: event.data.meta as StudioData,
        };
      case AttachmentTypeEnum.FILE:
        return {
          ...rfiAttachments,
          [event.data.source]: rfiAttachments[AttachmentTypeEnum.FILE].filter(
            (item) => {
              return item.file !== event.data.item;
            }
          ),
        };
      default:
        return {
          ...rfiAttachments,
          [event.data.source]: (
            rfiAttachments[event.data.source] as any
          ).filter((item: unknown) => item !== event.data.item),
        };
    }
  },
  sendForwardReply: async (context) => {
    const { rfi, forwardReply } = context;
    const { message, attachments } = forwardReply;

    try {
      const forwardActivity = new NewRFIForwardActivity({
        projectid: rfi.projectid,
        rfiid: rfi.id,
        message,
        pageids: attachments.DRAWING,
        attachments: List<NewRFIAttachment>(),
      });

      await rfiManager.createForwardActivity({
        rfi,
        projectid: rfi.projectid,
        fileAttachments: attachments.FILE,
        driveAttachments: attachments.DRIVE,
        integrationAttachments: attachments.INTEGRATION,
        studioAttachments: attachments.STUDIO,
        studioData: attachments.studioData,
        forwardActivity,
      });
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(rfi.id);
    } catch (err) {
      console.log('Unable to forward RFI', err);
      if (err instanceof Error) {
        const errorToast = err.message.includes('RFI Forward reply error')
          ? toastText.rfiForwardReplyError
          : toastText.rfiRespondError;

        toast.error(errorToast.message, errorToast.title);
      }
    }
    return rfi;
  },
  sendForwardMessage: async (context) => {
    const { rfi, forwardMessage } = context;
    const { forwardList, message, attachments } = forwardMessage;

    try {
      if (forwardList.size !== 0) {
        await rfiManager.modifyForwardingList(
          rfi.id,
          rfi.projectid,
          forwardList
        );
      }

      const forwardActivity = new NewRFIForwardActivity({
        projectid: rfi.projectid,
        rfiid: rfi.id,
        message,
        pageids: attachments.DRAWING,
        attachments: List<NewRFIAttachment>(),
      });

      await rfiManager.createForwardActivity({
        rfi,
        projectid: rfi.projectid,
        fileAttachments: attachments.FILE,
        driveAttachments: attachments.DRIVE,
        integrationAttachments: attachments.INTEGRATION,
        studioAttachments: attachments.STUDIO,
        studioData: attachments.studioData,
        forwardActivity,
      });

      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(rfi.id);
    } catch (err) {
      console.log('Unable to forward RFI', err);
      if (err instanceof Error) {
        const errorToast = err.message.includes('RFI Forward message error')
          ? toastText.rfiForwardMessageError
          : toastText.rfiRespondError;

        toast.error(errorToast.message, errorToast.title);
      }
    }
    return rfi;
  },
  sendOfficialResponse: async (context) => {
    const { rfi, officialResponse, currentUser } = context;

    try {
      if (officialResponse.message.length > 0) {
        const completeAnswer = new RFIAnswer({
          answer: officialResponse.message,
          pageids: officialResponse.attachments.DRAWING,
          projectid: rfi.projectid,
          respondingdate: Math.floor(Date.now() / 1000),
          reviewingemail: currentUser?.email,
          reviewingname: `${currentUser?.profile.firstname} ${currentUser?.profile.lastname}`,
          reviewingcompany: currentUser?.profile.company,
        });

        const { attachments } = officialResponse;
        await rfiManager.createOfficialResponse({
          rfi,
          response: completeAnswer,
          fileAttachments: attachments.FILE,
          driveAttachments: attachments.DRIVE,
          integrationAttachments: attachments.INTEGRATION,
          studioAttachments: attachments.STUDIO,
          studioData: attachments.studioData,
        });

        await rfiManager.refreshFilteredRFIs();

        return rfiManager.getRFI(rfi.id);
      }
    } catch (err) {
      console.log('Unable to create RFI Response', err);
      toast.error(
        toastText.rfiRespondError.message,
        toastText.rfiRespondError.title
      );
    }
    return rfi;
  },
  attachDiscussionToResponse: async (context) => {
    const { rfi, selectedDiscussionIds } = context;

    try {
      await rfiManager.attachForwardDiscussions(
        rfi,
        rfi.projectid,
        selectedDiscussionIds
      );
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(rfi.id);
    } catch (err) {
      console.log('Unable to attach RFI discussion', err);
      toast.error(
        toastText.rfiAttachDiscussionError.message,
        toastText.rfiAttachDiscussionError.title
      );
    }

    return rfi;
  },
  deleteAttachedDiscussion: async (context) => {
    const { rfi, selectedDiscussionIds } = context;

    if (selectedDiscussionIds.size !== 1)
      throw TypeError('Can only delete one discussion at a time.');

    try {
      await rfiManager.deleteForwardDiscussion(
        rfi.id,
        rfi.projectid,
        selectedDiscussionIds.first()
      );
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(rfi.id);
    } catch (err) {
      console.log('Unable to delete RFI discussion', err);
      toast.error(
        toastText.rfiAttachDiscussionError.message,
        toastText.rfiAttachDiscussionError.title
      );
    }

    return rfi;
  },
  reopenRfi: async (context) => {
    const { rfiRevision } = context;
    const { FILE, DRIVE, STUDIO, DRAWING, INTEGRATION, studioData } =
      rfiRevision.attachments;

    const newRfiVersion = new NewRFI({
      projectid: rfiRevision.rfi.projectid,
      title: rfiRevision.rfi.title,
      question: rfiRevision.rfi.question,
      duedate: rfiRevision.rfi.duedate,
      assignees: List([rfiRevision.rfi.assigneeEmail]),
      attachmentcount: 0,
      pageids: DRAWING,
      submittingname: rfiRevision.rfi.submittingname,
      submittingemail: rfiRevision.rfi.submittingemail,
      submittingcompany: rfiRevision.rfi.submittingcompany,
      distributionlist: rfiRevision.rfi.distributionlist,
      impactcategory: rfiRevision.rfi.impactcategory.kind,
      previousversionid: rfiRevision.rfi.previousversionid,
    });

    const rfiBaseId = rfiRevision.rfi.previousversionid.split('+')[0];
    const newRfiId = `${rfiBaseId}+${rfiRevision.rfi.versionnumber}`;

    try {
      await rfiManager.reopenRfi(
        newRfiVersion,
        FILE,
        DRIVE,
        INTEGRATION,
        STUDIO,
        studioData
      );
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(newRfiId);
    } catch (err) {
      console.log('Unable to reopen RFI', err);
      toast.error(
        toastText.rfiReopenError.message,
        toastText.rfiReopenError.title
      );
    }

    return rfiRevision.rfi;
  },
  saveDraft: async ({ rfiRevision }) => {
    const { attachments } = rfiRevision;
    const newRfi = rfiRevision.rfi
      .toNewRFI()
      .set('assignees', List([rfiRevision.rfi.assigneeEmail]))
      .set('pageids', attachments.DRAWING);

    try {
      await rfiManager.createRFIDraft(
        newRfi,
        attachments[AttachmentTypeEnum.FILE],
        attachments[AttachmentTypeEnum.DRIVE],
        attachments[AttachmentTypeEnum.INTEGRATION],
        attachments[AttachmentTypeEnum.STUDIO],
        attachments.studioData
      );
      await rfiManager.refreshFilteredRFIs();
    } catch (err) {
      console.log('Unable to save RFI draft', err);
      toast.error(
        toastText.rfiDraftSaveError.message,
        toastText.rfiDraftSaveError.title
      );
    }
  },
  updateDraft: async ({ rfiRevision }) => {
    const newRfi = rfiRevision.rfi
      .toNewRFI()
      .set('assignees', List([rfiRevision.rfi.assigneeEmail]))
      .set('pageids', rfiRevision.attachments[AttachmentTypeEnum.DRAWING]);

    try {
      await rfiManager.updateRFIDraft(
        rfiRevision.rfi.id,
        newRfi,
        rfiRevision.attachments[AttachmentTypeEnum.FILE],
        rfiRevision.attachmentsToRemove,
        rfiRevision.attachments[AttachmentTypeEnum.DRIVE],
        rfiRevision.attachments[AttachmentTypeEnum.INTEGRATION],
        rfiRevision.attachments[AttachmentTypeEnum.STUDIO],
        rfiRevision.attachments.studioData
      );
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(rfiRevision.rfi.id);
    } catch (err) {
      console.log('Unable to save RFI draft', err);
      toast.error(
        toastText.rfiDraftSaveError.message,
        toastText.rfiDraftSaveError.title
      );
    }
    return rfiRevision.rfi;
  },
  publishDraft: async ({ rfiRevision }) => {
    const newrfi = rfiRevision.rfi
      .toNewRFI()
      .set('assignees', List([rfiRevision.rfi.assigneeEmail]))
      .set('pageids', rfiRevision.attachments.DRAWING);

    const rfiBaseId = rfiRevision.rfi.previousversionid.split('+')[0];
    const newRfiId = `${rfiBaseId}+${rfiRevision.rfi.versionnumber}`;

    try {
      await rfiManager.publishRFIDraft(
        rfiRevision.rfi.id,
        newrfi,
        rfiRevision.attachments[AttachmentTypeEnum.FILE],
        rfiRevision.attachmentsToRemove,
        rfiRevision.attachments[AttachmentTypeEnum.DRIVE],
        rfiRevision.attachments[AttachmentTypeEnum.INTEGRATION],
        rfiRevision.attachments[AttachmentTypeEnum.STUDIO],
        rfiRevision.attachments.studioData
      );
      await rfiManager.refreshFilteredRFIs();
      return rfiManager.getRFI(newRfiId);
    } catch (err) {
      console.log('Unable to publish RFI draft', err);
      toast.error(
        toastText.rfiDraftPublishError.message,
        toastText.rfiDraftPublishError.title
      );
    }

    return rfiRevision.rfi;
  },
  deleteDraft: async ({ rfi }) => {
    const { id, projectid, previousversionid } = rfi;
    try {
      await rfiManager.deleteRFIDraft(id, projectid, previousversionid);
      await rfiManager.refreshFilteredRFIs();
    } catch (err) {
      console.log('Unable to delete RFI draft', err);
      toast.error(
        toastText.rfiDraftDeleteError.message,
        toastText.rfiDraftDeleteError.title
      );
    }
  },
});

export { createViewRfiServices };

function getRfiFileList(
  additionalFiles: FileList,
  fileAttachments: List<RFIFile>,
  projectid: string
) {
  const newRfiFiles = Array.from(additionalFiles).reduce(
    (rfiFilesAcc, file) => {
      if (file.name) {
        const isImageMimeType = imageMimeTypes.has(file.type);

        return rfiFilesAcc.push(
          new RFIFile({
            projectid,
            file,
            category: isImageMimeType ? 'imageattachment' : 'document',
            type: isImageMimeType ? 'IMAGE' : 'FILE',
          })
        );
      }
      return rfiFilesAcc;
    },
    List<RFIFile>()
  );

  const updatedRfiFiles = fileAttachments.concat(newRfiFiles);
  return updatedRfiFiles;
}
